import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initializeStrapiBackend } from '../Contentful_API/strapiBackend';

/**
 * Reports each page view to the CMS visitor analytics. The CMS works out country, device,
 * browser and IP itself; the website only sends the page, referrer, language and two random IDs:
 * one per browser (unique visitors) and one per visit (sessions, ending after 30 idle minutes).
 */

const SESSION_IDLE_MS = 30 * 60 * 1000;

function randomId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function visitorId() {
    try {
        let id = window.localStorage.getItem('mof_visitor_id');
        if (!id) {
            id = randomId();
            window.localStorage.setItem('mof_visitor_id', id);
        }
        return id;
    } catch {
        return undefined;
    }
}

function sessionId(now = Date.now()) {
    try {
        const saved = JSON.parse(window.sessionStorage.getItem('mof_session') || 'null');
        const id = saved && now - saved.last < SESSION_IDLE_MS ? saved.id : randomId();
        window.sessionStorage.setItem('mof_session', JSON.stringify({ id, last: now }));
        return id;
    } catch {
        return undefined;
    }
}

export async function sendPageView(path, { referrer = document.referrer } = {}) {
    try {
        const { apiUrl } = await initializeStrapiBackend();
        await fetch(`${apiUrl}/visitor-analytics/visits`, {
            method: 'POST',
            credentials: 'omit',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                path,
                referrer: referrer || undefined,
                language: navigator.language,
                visitorId: visitorId(),
                sessionId: sessionId(),
            }),
        });
    } catch {
        // Analytics must never affect the page.
    }
}

export default function PageViewTracker() {
    const { pathname } = useLocation();
    const last = useRef(null);
    const first = useRef(true);

    useEffect(() => {
        if (last.current === pathname) return;
        last.current = pathname;
        // Only the first page of a visit has an external referrer; later pages are internal navigation.
        sendPageView(pathname, first.current ? {} : { referrer: '' });
        first.current = false;
    }, [pathname]);

    return null;
}
